// Payment Review — super_admin. Pick vendor + period → readiness checklist
// → approve & freeze the settlement. Settled periods never silently change:
// the page shows settled-vs-recomputed drift instead. Every count is
// clickable (shared drill modal) and every list exports.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { settlementsApi, vendorApi, downloadAuthed } from '../api/services';
import InfoTip from '../components/InfoTip';
import DrillModal, { useDrill } from '../components/DrillModal';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  Loader2, BadgeCheck, Download, AlertTriangle, CheckCircle2,
  XCircle, Lock, Trash2,
} from 'lucide-react';

function fmtInr(n) {
  if (n == null || isNaN(n)) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function monthRange(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  const from = d.toISOString().slice(0, 10);
  const end = new Date(d);
  end.setMonth(end.getMonth() + 1);
  end.setDate(0);
  const today = new Date().toISOString().slice(0, 10);
  const to = end.toISOString().slice(0, 10) < today ? end.toISOString().slice(0, 10) : today;
  return { from, to };
}

// Drill metric per checklist item — counts must open their order lines.
const CHECK_DRILL = {
  unpriced: 'unpriced_lines_mtd',
  needs_review: 'needs_review_reships',
};

export default function PaymentReview() {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [{ from, to }, setRange] = useState(monthRange(0));
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [notes, setNotes] = useState('');
  const [acceptWarnings, setAcceptWarnings] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const { drill, drillData, drillLoading, openDrill, exportDrill, closeDrill } = useDrill();

  useEffect(() => {
    vendorApi.list().then((r) => {
      setVendors(r.data || []);
      if ((r.data || []).length > 0) setVendorId(r.data[0].id);
    }).catch(() => {});
    loadSettlements();
  }, []);

  function loadSettlements() {
    settlementsApi.list().then((r) => setSettlements(r.data.settlements || [])).catch(() => {});
  }

  useEffect(() => {
    if (!vendorId) return;
    setLoading(true);
    setReview(null);
    setAcceptWarnings(false);
    settlementsApi.review({ vendorId, from, to })
      .then((r) => setReview(r.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to build review'))
      .finally(() => setLoading(false));
  }, [vendorId, from, to]);

  async function approve() {
    setApproving(true);
    try {
      const res = await settlementsApi.approve({
        vendorId, from, to, notes: notes || null, acceptWarnings,
      });
      toast.success(`Settled ${fmtInr(res.data.totalDue)} — frozen.`);
      setNotes('');
      loadSettlements();
      const r = await settlementsApi.review({ vendorId, from, to });
      setReview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Approval failed');
    } finally { setApproving(false); }
  }

  async function removeSettlement(id) {
    if (!window.confirm('Undo this sign-off? The period becomes open again (this is audited).')) return;
    try {
      await settlementsApi.remove(id);
      toast.success('Settlement removed.');
      loadSettlements();
      const r = await settlementsApi.review({ vendorId, from, to });
      setReview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  }

  const s = review?.summary;
  const settled = review?.settlement;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <BadgeCheck className="w-5 h-5 text-emerald-600" />
        <h1 className="text-2xl font-semibold text-gray-900">Payment Review</h1>
        <InfoTip title="How payment review works" align="left">
          Pick a vendor and a period. The checklist tells you whether the
          total can be trusted (unpriced or undecided lines bill ₹0 and
          understate it). When you approve, the amount and every line behind
          it are <b>frozen as the settlement</b> — later price corrections
          can never silently change a settled bill; this page would show the
          difference instead. Undoing a sign-off is possible and audited.
        </InfoTip>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        The pre-payment check: trust the number, then lock it.
      </p>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-gray-500 mb-1">Vendor</span>
          <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-gray-500 mb-1">From</span>
          <input type="date" value={from} onChange={(e) => setRange({ from: e.target.value, to })}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-gray-500 mb-1">To</span>
          <input type="date" value={to} onChange={(e) => setRange({ from, to: e.target.value })}
                 className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="flex gap-2">
          <button onClick={() => setRange(monthRange(0))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">This month</button>
          <button onClick={() => setRange(monthRange(-1))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Last month</button>
        </div>
      </div>

      {loading && (
        <div className="py-8 text-center text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Building review…
        </div>
      )}

      {review && !loading && (
        <>
          {/* Settled banner with drift */}
          {settled && (
            <div className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
              Math.abs(settled.driftInr) > 0.01
                ? 'border-amber-300 bg-amber-50 text-amber-900'
                : 'border-emerald-300 bg-emerald-50 text-emerald-900'
            }`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Lock className="w-4 h-4" />
                <span>
                  <strong>Settled at {fmtInr(settled.totalDue)}</strong> on{' '}
                  {format(new Date(settled.approvedAt), 'dd MMM yyyy HH:mm')}
                  {settled.approvedBy && <> by {settled.approvedBy}</>}.
                </span>
                {Math.abs(settled.driftInr) > 0.01 ? (
                  <span>
                    Recomputing today gives <strong>{fmtInr(settled.recomputedTotalDue)}</strong> —
                    a difference of <strong>{fmtInr(settled.driftInr)}</strong> (data changed after
                    sign-off; investigate before paying anything extra).
                  </span>
                ) : (
                  <span>Recomputed today: identical. Safe.</span>
                )}
                {settled.notes && <span className="text-xs opacity-75">Note: {settled.notes}</span>}
              </div>
            </div>
          )}

          {/* The number */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500">
                {review.vendor.name} · {from} → {to}{' '}
                <InfoTip title="How this total is calculated">
                  For every product line this vendor dispatched in the period:
                  quantity × the INR price <b>in effect on the dispatch date</b>
                  (a later price change never alters an older bill). Shipping is
                  per parcel from the destination rate card. Reships bill at the
                  vendor's reship policy (e.g. 50% when lost in transit).
                  Unpriced and "vendor decides" lines add ₹0 — see the checklist.
                </InfoTip>
              </div>
              <div className="text-3xl font-semibold text-gray-900 tabular-nums">{fmtInr(s.total_due)}</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {fmtInr(s.priced_line_cost)} products + {fmtInr(s.shipping_cost)} shipping ·{' '}
                {s.orders} order(s) · {s.shipments} parcel(s) · {s.items} line(s)
                {s.reship?.orders > 0 && (
                  <> · <span className="text-violet-700">{fmtInr(s.reship.total_due)} reships ({s.reship.orders} ord)</span></>
                )}
              </div>
            </div>
            <Link to={`/admin/vendor-payables?vendorId=${vendorId}&from=${from}&to=${to}`}
                  className="text-sm text-blue-600 hover:underline">
              line-by-line statement →
            </Link>
          </div>

          {/* Checklist */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
              Can this number be trusted?{' '}
              <InfoTip title="Reading the checklist">
                <b>Red items are blockers</b> — lines billing ₹0 that make the
                total understated; settling with them open needs an explicit
                acknowledgement. <b>Amber items are warnings</b> — things that
                could hide a price (pending mappings) or use a fallback rate
                (flat-rate parcels); review them but they don't block.
                Click any failed item to see the exact lines, with export.
              </InfoTip>
            </div>
            <ul className="divide-y divide-gray-100">
              {review.checklist.map((c) => {
                const drillKey = CHECK_DRILL[c.key];
                const clickable = !c.ok && drillKey;
                const Row = clickable ? 'button' : 'div';
                return (
                  <li key={c.key}>
                    <Row onClick={clickable ? () => openDrill(drillKey) : undefined}
                         title={clickable ? 'See the exact lines' : undefined}
                         className={`w-full text-left px-4 py-3 flex items-start gap-3 ${clickable ? 'hover:bg-rose-50/50' : ''}`}>
                      {c.ok
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                        : c.level === 'blocker'
                          ? <XCircle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
                          : <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />}
                      <div className="flex-1">
                        <div className={`text-sm ${c.ok ? 'text-gray-700' : 'font-medium text-gray-900'}`}>
                          {c.label}
                          {!c.ok && (
                            <span className={`ml-2 text-xs font-semibold ${c.level === 'blocker' ? 'text-rose-700' : 'text-amber-700'}`}>
                              {c.count} {clickable && '· click to see them'}
                            </span>
                          )}
                        </div>
                        {!c.ok && <div className="text-xs text-gray-500 mt-0.5">{c.hint}</div>}
                      </div>
                    </Row>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Reships by reason for the period */}
          {review.reshipByReason.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
                Reships in this bill, by reason{' '}
                <InfoTip title="Reships by reason">
                  Why re-sends happened in this period and what each reason is
                  costing. <b>Billed</b> = what the reship policy charges us
                  (e.g. 50% when lost in transit). <b>Full price</b> = what the
                  same parcels would cost with no policy. <b>Saved</b> = the
                  difference. Lots of one reason = a conversation with the
                  vendor or India Post.
                </InfoTip>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2">Reason</th>
                    <th className="text-right px-3 py-2">Orders</th>
                    <th className="text-right px-3 py-2">Billed</th>
                    <th className="text-right px-3 py-2">Full price</th>
                    <th className="text-right px-3 py-2">Saved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {review.reshipByReason.map((a) => (
                    <tr key={a.reason}>
                      <td className="px-3 py-2 capitalize">{a.reason === 'no_reason' ? 'no reason recorded' : a.reason.replace(/_/g, ' ')}</td>
                      <td className="px-3 py-2 text-right">{a.orders}</td>
                      <td className="px-3 py-2 text-right font-semibold">{fmtInr(a.total_due)}</td>
                      <td className="px-3 py-2 text-right text-gray-500">{fmtInr(a.gross_value)}</td>
                      <td className="px-3 py-2 text-right text-violet-700">{fmtInr(Math.max(0, a.gross_value - a.total_due))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Approve */}
          {!settled && s.items > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
              <div className="text-sm font-semibold text-gray-900 mb-2">
                Sign off this payment{' '}
                <InfoTip title="What approving does">
                  Approving <b>freezes</b> this exact amount and every line
                  behind it as the settlement for this vendor + period. Later
                  price or policy corrections never change a settled bill —
                  if the data changes afterwards, this page shows the
                  difference instead. The note is a good place for the payment
                  reference (UTR). Undo is possible from the register below
                  and is recorded in the audit log.
                </InfoTip>
              </div>
              {review.blockers > 0 && (
                <label className="flex items-start gap-2 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3">
                  <input type="checkbox" checked={acceptWarnings}
                         onChange={(e) => setAcceptWarnings(e.target.checked)}
                         className="mt-0.5" />
                  <span>
                    I understand {review.blockers} blocker(s) are open — some lines bill ₹0,
                    so <b>{fmtInr(s.total_due)} is understated</b>. Settle anyway (any agreed
                    extra will need a separate settlement after fixing).
                  </span>
                </label>
              )}
              <div className="flex gap-2">
                <input value={notes} onChange={(e) => setNotes(e.target.value)}
                       placeholder="Optional note (e.g. UTR / payment reference, agreement with vendor)"
                       className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <button onClick={approve}
                        disabled={approving || (review.blockers > 0 && !acceptWarnings)}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                  {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  Approve & freeze {fmtInr(s.total_due)}
                </button>
              </div>
            </div>
          )}
          {!settled && s.items === 0 && (
            <div className="mb-6 text-sm text-gray-500 text-center py-4">
              No dispatched lines for this vendor in the period — nothing to settle.
            </div>
          )}
        </>
      )}

      {/* Settlement register */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-semibold">
          Settlement register{' '}
          <InfoTip title="Settlement register">
            Every payment sign-off, newest first, frozen exactly as approved.
            The download icon exports the line-by-line snapshot that was
            locked at approval — what you agreed to pay, verbatim. A ⚠ means
            it was knowingly approved with open blockers (understated). The
            bin icon undoes a sign-off — audited, and the period becomes
            open for re-approval.
          </InfoTip>
          <span className="ml-2 text-xs font-normal text-gray-500">everything signed off, frozen as approved</span>
        </div>
        {settlements.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500">Nothing settled yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Vendor</th>
                <th className="text-left px-3 py-2">Period</th>
                <th className="text-right px-3 py-2">Total settled</th>
                <th className="text-right px-3 py-2">Orders</th>
                <th className="text-left px-3 py-2">Approved</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {settlements.map((st) => (
                <tr key={st.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{st.vendor_name}</td>
                  <td className="px-3 py-2 text-gray-600">
                    {String(st.period_from).slice(0, 10)} → {String(st.period_to).slice(0, 10)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtInr(st.total_due)}</td>
                  <td className="px-3 py-2 text-right">{st.orders}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {format(new Date(st.approved_at), 'dd MMM yyyy')}{st.approved_by_name && ` · ${st.approved_by_name}`}
                    {(st.unpriced_lines > 0 || st.needs_review_lines > 0) && (
                      <span className="ml-1 text-amber-700" title="Approved with open blockers — understated total accepted">⚠</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => downloadAuthed(`/admin/settlements/${st.id}/export.csv`, `SETTLEMENT-${st.vendor_name}-${String(st.period_from).slice(0, 10)}.csv`)
                              .catch(() => toast.error('Download failed'))}
                            title="Download the frozen line snapshot"
                            className="text-gray-500 hover:text-blue-600 mr-2">
                      <Download className="w-4 h-4 inline" />
                    </button>
                    <button onClick={() => removeSettlement(st.id)}
                            title="Undo this sign-off (audited)"
                            className="text-gray-400 hover:text-rose-600">
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drill && (
        <DrillModal data={drillData} loading={drillLoading}
                    onClose={closeDrill} onExport={exportDrill} />
      )}
    </div>
  );
}
